import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, CalendarIcon, MapPinIcon, BellIcon, MessageSquareIcon, RecycleIcon, CheckCircle2Icon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const COMPARISON_ROWS = [{
  traditional: {
    fr: 'Calendrier papier',
    en: 'Paper Calendar'
  },
  bciti: {
    fr: 'Adresse vérifiée',
    en: 'Verified Address'
  }
}, {
  traditional: {
    fr: 'PDF générique',
    en: 'Generic PDF'
  },
  bciti: {
    fr: 'Calendrier personnalisé',
    en: 'Personalized Calendar'
  }
}, {
  traditional: {
    fr: 'Qui est responsable\u00A0?',
    en: 'Who is responsible?'
  },
  bciti: {
    fr: 'La bonne réponse',
    en: 'The right answer'
  }
}, {
  traditional: {
    fr: 'Appeler le service à la clientèle',
    en: 'Call the service desk'
  },
  bciti: {
    fr: 'ATNA répond instantanément',
    en: 'ATNA responds instantly'
  }
}, {
  traditional: {
    fr: 'Confusion au tri',
    en: 'Sorting confusion'
  },
  bciti: {
    fr: 'Réponses de tri instantanées',
    en: 'Instant sorting answers'
  }
}, {
  traditional: {
    fr: 'Aucune visibilité',
    en: 'No visibility'
  },
  bciti: {
    fr: 'Analytique par secteur',
    en: 'Area-level analytics'
  }
}];
const WORKFLOW = [{
  fr: 'Adresse vérifiée',
  en: 'Verified Address'
}, {
  fr: 'Calendrier personnalisé',
  en: 'Personalized Calendar'
}, {
  fr: 'Usage hebdomadaire',
  en: 'Weekly Usage'
}, {
  fr: 'Foyer engagé',
  en: 'Engaged Household'
}, {
  fr: 'Socle des services futurs',
  en: 'Foundation For Future Services'
}];
export function Collect() {
  const {
    lang
  } = useLanguage();
  const t = (fr: string, en: string) => lang === 'FR' ? fr : en;
  useEffect(() => {
    track('Page Viewed', {
      page: 'Collect'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-1469">
      <Header data-id="element-1470" />

      <main className="pt-[120px]" data-id="element-1471">
        {/* SECTION 1: HOOK */}
        <section className="pt-24 pb-32 px-6" data-id="element-1472">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1473">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-1474">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-6" data-id="element-1475">
                {t('BCITI Collecte', 'BCITI Collect')}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-1476">
                {t('Le bon bac. Le bon jour. La bonne réponse.', 'The right bin. The right day. The right answer.')}
              </h1>
              <p className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-4" data-id="element-1477">
                {t("Des calendriers de collecte personnalisés selon l'adresse vérifiée.", 'Personalized collection schedules by verified address.')}
              </p>
              <p className="font-sans text-lg text-[#121212]/60 leading-relaxed mb-10" data-id="element-1478">
                {t('ATNA répond aux questions du quotidien — peu importe qui exploite le service.', 'ATNA answers everyday questions — no matter who operates the service.')}
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-10" data-id="element-1479">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Collect Hero'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-1480">
                  {t('Demander une démo', 'Request a Demo')}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1481" />
                </Link>
                <a href="#proof" onClick={e => {
                e.preventDefault();
                document.getElementById('proof')?.scrollIntoView({
                  behavior: 'smooth'
                });
                track('Proof CTA Clicked', {
                  location: 'Collect Hero'
                });
              }} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-1482">
                  {t('Voir en production', 'See It In Production')}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1483" />
                </a>
              </div>

              <div className="flex flex-wrap items-center gap-3 font-sans text-xs font-medium text-[#121212]/50 tracking-wider uppercase" data-id="element-1484">
                <span data-id="element-1485">{t('Recyclage', 'Recycling')}</span>
                <span className="w-1 h-1 rounded-full bg-[#E4E6EC]" data-id="element-1486" />
                <span data-id="element-1487">{t('Déchets', 'Waste')}</span>
                <span className="w-1 h-1 rounded-full bg-[#E4E6EC]" data-id="element-1488" />
                <span data-id="element-1489">{t('Encombrants', 'Bulky Items')}</span>
                <span className="w-1 h-1 rounded-full bg-[#E4E6EC]" data-id="element-1490" />
                <span data-id="element-1491">{t('Résidus verts', 'Yard Waste')}</span>
                <span className="w-1 h-1 rounded-full bg-[#E4E6EC]" data-id="element-1492" />
                <span data-id="element-1493">{t('Points de dépôt', 'Drop-Off Locations')}</span>
              </div>
            </motion.div>

            {/* HERO VISUAL: Resident experience (product, not workflow) */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="flex justify-center" data-id="element-1494">
              <div className="w-full max-w-sm border border-[#E4E6EC] bg-[#FAFAFB] p-4" data-id="element-1495">
                <div className="border border-[#E4E6EC] bg-white" data-id="element-1496">
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E6EC]" data-id="element-1497">
                    <div className="flex items-center gap-2" data-id="element-1498">
                      <span className="inline-block w-3 h-3 bg-[#4F46E5]" aria-hidden="true" data-id="element-1499" />
                      <span className="font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#121212]/70" data-id="element-1500">
                        {t('Collecte', 'Collect')}
                      </span>
                    </div>
                    <span className="font-sans text-[11px] text-[#121212]/40 flex items-center gap-1" data-id="element-1501">
                      <MapPinIcon className="w-3 h-3" data-id="element-1502" />
                      24 rue des Érables
                    </span>
                  </div>

                  {/* Personalized calendar */}
                  <div className="px-5 py-6 border-b border-[#E4E6EC]" data-id="element-1503">
                    <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-3" data-id="element-1504">
                      {t('Cette semaine', 'This week')}
                    </div>
                    <div className="grid grid-cols-7 gap-1.5 mb-4" data-id="element-1505">
                      {(lang === 'FR' ? ['L', 'M', 'M', 'J', 'V', 'S', 'D'] : ['M', 'T', 'W', 'T', 'F', 'S', 'S']).map((d, i) => <div key={i} className="text-center" data-id="element-1506">
                          <div className="font-sans text-[10px] text-[#121212]/40 mb-1" data-id="element-1507">
                            {d}
                          </div>
                          <div className={`h-7 flex items-center justify-center text-[10px] font-sans border ${i === 2 ? 'border-[#4F46E5] bg-[#4F46E5]/5 text-[#4F46E5] font-semibold' : 'border-[#E4E6EC] text-[#121212]/50'}`} data-id="element-1508">
                            {i === 2 ? <RecycleIcon className="w-3 h-3" data-id="element-1509" /> : ''}
                          </div>
                        </div>)}
                    </div>
                    <div className="flex items-center gap-2 text-[#121212]" data-id="element-1510">
                      <span className="font-serif text-lg" data-id="element-1511">
                        {t('Recyclage — mercredi', 'Recycling — Wednesday')}
                      </span>
                    </div>
                  </div>

                  {/* Reminder */}
                  <div className="px-5 py-4 border-b border-[#E4E6EC] flex items-start gap-3" data-id="element-1512">
                    <BellIcon className="w-4 h-4 text-[#4F46E5] shrink-0 mt-0.5" data-id="element-1513" />
                    <div data-id="element-1514">
                      <div className="font-sans text-sm font-medium text-[#121212]" data-id="element-1515">
                        {t('Rappel activé', 'Reminder set')}
                      </div>
                      <div className="font-sans text-xs text-[#121212]/55" data-id="element-1516">
                        {t('Recyclage à sortir ce soir · 19 h 00', 'Recycling out tonight · 7:00 PM')}
                      </div>
                    </div>
                  </div>

                  {/* Assistant question & answer */}
                  <div className="px-5 py-4 space-y-3" data-id="element-1517">
                    <div className="flex items-start gap-3 justify-end" data-id="element-1518">
                      <div className="bg-[#FAFAFB] border border-[#E4E6EC] px-3 py-2 font-sans text-xs text-[#121212]/70 max-w-[80%]" data-id="element-1519">
                        {t('Quel bac pour les boîtes à pizza ?', 'Which bin for pizza boxes?')}
                      </div>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-1520">
                      <MessageSquareIcon className="w-4 h-4 text-[#4F46E5] shrink-0 mt-1" data-id="element-1521" />
                      <div className="bg-[#4F46E5]/5 border border-[#4F46E5]/20 px-3 py-2 font-sans text-xs text-[#121212] max-w-[80%]" data-id="element-1522">
                        {t('Le carton propre va au recyclage. Les parties grasses vont aux déchets.', 'Clean cardboard goes in Recycling. Greasy parts go in Waste.')}
                      </div>
                    </div>
                  </div>
                </div>
                <p className="font-sans text-[11px] text-[#121212]/40 text-center mt-4" data-id="element-1523">
                  {t('Vue illustrative du produit', 'Illustrative product view')}
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: CONTRAST */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1524">
          <div className="max-w-4xl mx-auto" data-id="element-1525">
            <div className="text-center mb-20" data-id="element-1526">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6 leading-tight" data-id="element-1527">
                {t('Le calendrier papier informe. Collecte répond.', 'Paper Calendar informs. Collect answers.')}
              </motion.h2>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-sans text-xl text-[#121212]/60 max-w-2xl mx-auto" data-id="element-1528">
                {t('Les résidents ne devraient pas avoir à deviner qui appeler.', 'Residents should not have to guess who to call.')}
              </motion.p>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="bg-white border border-[#E4E6EC] overflow-hidden mb-12" data-id="element-1529">
              <div className="grid grid-cols-2 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1530">
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase border-r border-[#E4E6EC]" data-id="element-1531">
                  {t('Traditionnel', 'Traditional')}
                </div>
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase" data-id="element-1532">
                  {t('BCITI Collecte', 'BCITI Collect')}
                </div>
              </div>
              {COMPARISON_ROWS.map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-[#E4E6EC] last:border-0 hover:bg-[#FAFAFB]/50 transition-colors" data-id="element-1533">
                  <div className="p-6 font-serif text-lg text-[#121212]/50 border-r border-[#E4E6EC]" data-id="element-1534">
                    {t(row.traditional.fr, row.traditional.en)}
                  </div>
                  <div className="p-6 font-serif text-lg text-[#121212]" data-id="element-1535">
                    {t(row.bciti.fr, row.bciti.en)}
                  </div>
                </div>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center max-w-2xl mx-auto" data-id="element-1536">
              <div className="w-16 h-px bg-[#E4E6EC] mx-auto mb-8" aria-hidden="true" data-id="element-1537" />
              <p className="font-serif text-3xl text-[#121212]" data-id="element-1538">
                {t("L'information est utile.", 'Information is useful.')}
              </p>
              <p className="font-serif text-3xl text-[#121212]/50 mt-2" data-id="element-1539">
                {t("Les réponses, c'est mieux.", 'Answers are better.')}
              </p>
              <p className="font-sans text-lg text-[#121212]/60 mt-8" data-id="element-1540">
                {t('Les résidents veulent des réponses, pas des structures organisationnelles.', 'Residents care about answers. Not organizational structures.')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2b: IDENTITY CREATES CONTEXT — text-only transition */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-1541">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1542">
            <motion.span {...reveal} className="block font-sans text-xs font-semibold tracking-[0.25em] text-indigo uppercase mb-8" data-id="element-1543">
              {t("L'identité crée le contexte", 'Identity Creates Context')}
            </motion.span>

            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="font-serif text-2xl text-[#121212] max-w-2xl mx-auto leading-snug" data-id="element-1544">
              {t('Tout le monde publie un calendrier. Le nôtre connaît votre adresse — et répond, peu importe qui exploite le service.', 'Everyone publishes a schedule. Ours knows your address — and answers, whoever runs the service.')}
            </motion.p>
          </div>
        </section>

        {/* SECTION 3: THE HOUSEHOLD FOUNDATION */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-1545">
          <div className="max-w-6xl mx-auto" data-id="element-1546">
            <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-1547">
              <motion.span {...reveal} className="block font-sans text-xs font-semibold tracking-[0.25em] text-[#4F46E5] uppercase mb-8" data-id="element-1548">
                {t('Le socle du foyer.', 'The Household Foundation.')}
              </motion.span>
              <motion.h2 {...reveal} transition={{
              duration: 0.7,
              delay: 0.05
            }} className="font-serif text-4xl md:text-5xl text-[#121212] mb-8 leading-tight" data-id="element-1549">
                {t('Le service que chaque foyer ouvre. Chaque semaine. Pour toujours.', 'The service every household opens. Every week. Forever.')}
              </motion.h2>
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="space-y-6 font-sans text-xl text-[#121212]/70 leading-relaxed" data-id="element-1550">
                <p data-id="element-1551">
                  {t('Les résidents enregistrent une adresse vérifiée pour accéder à leur calendrier de collecte personnalisé.', 'Residents register a verified address to access their personalized collection schedule.')}
                </p>
                <p data-id="element-1552">
                  {t('Ce foyer vérifié devient le socle de chaque interaction future.', 'That verified household becomes the foundation for every future interaction.')}
                </p>
                <p data-id="element-1553">
                  {t('Communications le rejoint. Requêtes le sert. Analytique le mesure. ATNA en apprend.', 'Communications reaches it. Requests serves it. Analytics measures it. ATNA learns from it.')}
                </p>
              </motion.div>
            </div>

            <div className="mb-20" data-id="element-1554">
              {/* SINGLE WORKFLOW */}
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.2
            }} className="flex flex-col items-center" data-id="element-1555">
                {WORKFLOW.map((node, i, arr) => <Fragment key={node.en} data-id="element-1556">
                    <div className={`w-full max-w-md py-4 px-6 text-center border font-sans text-sm ${i === 0 ? 'border-[#4F46E5] bg-[#4F46E5]/5 text-[#4F46E5] font-medium' : i === arr.length - 1 ? 'border-[#121212] bg-[#121212] text-white font-medium' : 'border-[#E4E6EC] bg-white text-[#121212]'}`} data-id="element-1557">
                      {t(node.fr, node.en)}
                    </div>
                    {i < arr.length - 1 && <ArrowDownIcon className="w-4 h-4 text-[#E4E6EC] my-2" data-id="element-1558" />}
                  </Fragment>)}
              </motion.div>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.4
          }} className="text-center max-w-2xl mx-auto" data-id="element-1559">
              <p className="font-serif text-2xl text-[#121212]/50" data-id="element-1560">
                {t("La collecte n'est pas un service de plus.", 'Collection is not one more service.')}
              </p>
              <p className="font-serif text-2xl text-[#121212] mt-1" data-id="element-1561">
                {t("C'est la porte d'entrée du foyer.", 'It is the front door to the household.')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 4: PROOF */}
        <section id="proof" className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB] scroll-mt-24" data-id="element-1562">
          <div className="max-w-5xl mx-auto" data-id="element-1563">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-16" data-id="element-1564">
              {t('En production. Mesurée.', 'In production. Measured.')}
            </motion.h2>

            {/* Proof cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC] mb-8" data-id="element-1565">
              {[{
              stat: 'Clarence-Rockland',
              label: {
                fr: 'Collecte personnalisée en production',
                en: 'Personalized Collection Live'
              },
              note: {
                fr: 'Assistant ATNA en production ici',
                en: 'ATNA Assistant in production here'
              },
              noteClass: 'text-[#4F46E5]'
            }, {
              stat: '20+',
              label: {
                fr: 'Communautés sur le réseau',
                en: 'Communities on the Network'
              },
              note: {
                fr: 'Module Collecte en production — calendriers de collecte par adresse vérifiée',
                en: 'Collect module live — verified-address collection schedules'
              },
              noteClass: 'text-[#121212]/45'
            }, {
              stat: {
                fr: 'Depuis 2017',
                en: 'Since 2017'
              },
              label: {
                fr: 'Infrastructure éprouvée',
                en: 'Proven Infrastructure'
              }
            }].map((card, i) => <div key={i} className="bg-white p-10" data-id="element-1566">
                  <div className="font-serif text-3xl md:text-4xl text-[#121212] mb-3" data-id="element-1567">
                    {typeof card.stat === 'string' ? card.stat : t(card.stat.fr, card.stat.en)}
                  </div>
                  <div className="font-sans text-sm font-semibold tracking-wide text-[#4F46E5] uppercase" data-id="element-1568">
                    {t(card.label.fr, card.label.en)}
                  </div>
                  {card.note && <div className={`font-sans text-xs mt-3 ${card.noteClass}`} data-id="element-1569">
                      {t(card.note.fr, card.note.en)}
                    </div>}
                </div>)}
            </div>

            <p className="font-sans text-lg text-[#121212]/70 text-center max-w-3xl mx-auto mb-16" data-id="element-1570">
              {t('Les services de collecte fonctionnent sur un réseau de communautés bâti sur des adresses vérifiées et une infrastructure numérique de confiance.', 'Collection services operate across a network of communities built on verified addresses and trusted digital infrastructure.')}
            </p>

            {/* Hairline divider — separates the live production stat cards from the research-based outcome */}
            <div className="w-full h-px bg-[#E4E6EC] mb-16" aria-hidden="true" data-id="element-1571" />

            {/* Research block */}
            <div className="bg-white border border-[#4F46E5]/20 p-12 relative shadow-sm" data-id="element-1572">
              <div className="absolute -top-4 left-12 bg-[#4F46E5] text-white font-sans text-xs font-bold tracking-widest uppercase px-4 py-2" data-id="element-1573">
                {t('Résultat fondé sur la recherche', 'Research-Based Outcome')}
              </div>
              {lang === 'FR' ? <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug mb-8 mt-2" data-id="element-1574">
                  Des campagnes éducatives diffusées par applications mobiles
                  ont réduit la contamination du recyclage de{' '}
                  <span className="text-[#4F46E5]" data-id="element-1575">24&nbsp;%</span> à moins de{' '}
                  <span className="text-[#4F46E5]" data-id="element-1576">4&nbsp;%</span> dans une
                  municipalité ontarienne.
                </p> : <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug mb-8 mt-2" data-id="element-1577">
                  Educational campaigns delivered through mobile applications
                  reduced recycling contamination from{' '}
                  <span className="text-[#4F46E5]" data-id="element-1578">24%</span> to under{' '}
                  <span className="text-[#4F46E5]" data-id="element-1579">4%</span> in an Ontario
                  municipality.
                </p>}
              <p className="font-sans text-xs text-[#121212]/45 leading-relaxed max-w-3xl" data-id="element-1580">
                {t("Résultats d'audits municipaux rapportés dans la littérature évaluée par les pairs sur les applications de recyclage résidentiel. Les résultats varient selon le déploiement et la participation.", 'Reported municipal audit results referenced in peer-reviewed literature on household recycling applications. Results vary by deployment and participation.')}
              </p>
            </div>

            {/* In-production CTA — confidence peak under the research outcome */}
            <div className="text-center mt-16" data-id="element-1581">
              <p className="font-serif text-2xl text-[#121212] mb-6" data-id="element-1582">
                {t("C'est en production aujourd'hui.", 'This is running in production today.')}
              </p>
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Collect In Production Proof'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-1583">
                {t('Demander une démo', 'Request a Demo')}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1584" />
              </Link>
            </div>

            {/* PRODUCTION SHOWCASE — Live Collection Experience */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="mt-20" data-id="element-1585">
              <h3 className="font-serif text-3xl text-[#121212] text-center mb-3" data-id="element-1586">
                {t('Expérience de collecte en direct', 'Live Collection Experience')}
              </h3>
              <p className="font-sans text-sm text-[#121212]/50 text-center mb-12" data-id="element-1587">
                {t('Exemple illustratif', 'Illustrative Example')}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E4E6EC] border border-[#E4E6EC]" data-id="element-1588">
                {/* Collection Calendar */}
                <div className="bg-white p-6" data-id="element-1589">
                  <div className="flex items-center gap-2 mb-5" data-id="element-1590">
                    <CalendarIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-1591" />
                    <span className="font-sans text-[11px] font-semibold tracking-[0.16em] uppercase text-[#121212]/50" data-id="element-1592">
                      {t('Calendrier de collecte', 'Collection Calendar')}
                    </span>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mb-4" data-id="element-1593">
                    {(lang === 'FR' ? ['L', 'M', 'M', 'J', 'V', 'S', 'D'] : ['M', 'T', 'W', 'T', 'F', 'S', 'S']).map((d, i) => <div key={i} className="text-center" data-id="element-1594">
                        <div className="font-sans text-[9px] text-[#121212]/40 mb-1" data-id="element-1595">
                          {d}
                        </div>
                        <div className={`h-6 flex items-center justify-center border ${i === 2 ? 'border-[#4F46E5] bg-[#4F46E5]/5' : 'border-[#E4E6EC]'}`} data-id="element-1596">
                          {i === 2 && <RecycleIcon className="w-3 h-3 text-[#4F46E5]" data-id="element-1597" />}
                        </div>
                      </div>)}
                  </div>
                  <div className="font-sans text-xs text-[#121212]/70" data-id="element-1598">
                    {t('Recyclage — mercredi', 'Recycling — Wednesday')}
                  </div>
                </div>

                {/* Reminder Screen */}
                <div className="bg-white p-6" data-id="element-1599">
                  <div className="flex items-center gap-2 mb-5" data-id="element-1600">
                    <BellIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-1601" />
                    <span className="font-sans text-[11px] font-semibold tracking-[0.16em] uppercase text-[#121212]/50" data-id="element-1602">
                      {t('Écran de rappel', 'Reminder Screen')}
                    </span>
                  </div>
                  <div className="border border-[#E4E6EC] p-4" data-id="element-1603">
                    <div className="font-sans text-sm font-medium text-[#121212] mb-1" data-id="element-1604">
                      {t('Recyclage à sortir ce soir', 'Recycling out tonight')}
                    </div>
                    <div className="font-sans text-xs text-[#121212]/55 mb-4" data-id="element-1605">
                      {t('24 rue des Érables · 19 h 00', '24 rue des Érables · 7:00 PM')}
                    </div>
                    <div className="flex items-center gap-2 text-[#4F46E5]" data-id="element-1606">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-1607" />
                      <span className="font-sans text-xs" data-id="element-1608">
                        {t('Rappel activé', 'Reminder set')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* ATNA Question Example */}
                <div className="bg-white p-6" data-id="element-1609">
                  <div className="flex items-center gap-2 mb-5" data-id="element-1610">
                    <MessageSquareIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-1611" />
                    <span className="font-sans text-[11px] font-semibold tracking-[0.16em] uppercase text-[#121212]/50" data-id="element-1612">
                      {t('Exemple de question ATNA', 'ATNA Question Example')}
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-1613">
                    <div className="flex justify-end" data-id="element-1614">
                      <div className="bg-[#FAFAFB] border border-[#E4E6EC] px-3 py-2 font-sans text-xs text-[#121212]/70 max-w-[85%]" data-id="element-1615">
                        {t('Quel bac pour les boîtes à pizza ?', 'Which bin for pizza boxes?')}
                      </div>
                    </div>
                    <div className="flex" data-id="element-1616">
                      <div className="bg-[#4F46E5]/5 border border-[#4F46E5]/20 px-3 py-2 font-sans text-xs text-[#121212] max-w-[85%]" data-id="element-1617">
                        {t('Le carton propre va au recyclage. Les parties grasses vont aux déchets.', 'Clean cardboard goes in Recycling. Greasy parts go in Waste.')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* RecycleMap+ Example */}
                <div className="bg-white p-6" data-id="element-1618">
                  <div className="flex items-center gap-2 mb-5" data-id="element-1619">
                    <MapPinIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-1620" />
                    <span className="font-sans text-[11px] font-semibold tracking-[0.16em] uppercase text-[#121212]/50" data-id="element-1621">
                      {t('Exemple RecycleMap+', 'RecycleMap+ Example')}
                    </span>
                  </div>
                  <div className="space-y-2.5" data-id="element-1622">
                    {[{
                    fr: 'Écocentre municipal',
                    en: 'Municipal eco-centre'
                  }, {
                    fr: 'Dépôt producteur',
                    en: 'Producer drop-off'
                  }, {
                    fr: 'Dépôt privé',
                    en: 'Private depot'
                  }].map(site => <div key={site.en} className="flex items-center gap-2 border border-[#E4E6EC] px-3 py-2" data-id="element-1623">
                        <MapPinIcon className="w-3.5 h-3.5 text-[#4F46E5]/60 shrink-0" data-id="element-1624" />
                        <span className="font-sans text-xs text-[#121212]/70" data-id="element-1625">
                          {t(site.fr, site.en)}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 5: WHAT'S INSIDE */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-1626">
          <div className="max-w-6xl mx-auto" data-id="element-1627">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-20" data-id="element-1628">
              {t("Ce qu'il contient.", "What's inside.")}
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-1629">
              {/* PERSONALIZED CALENDAR */}
              <div className="border border-[#E4E6EC] p-8" data-id="element-1630">
                <div className="flex items-center gap-3 mb-6" data-id="element-1631">
                  <CalendarIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-1632" />
                  <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase" data-id="element-1633">
                    {t('Calendrier personnalisé', 'Personalized Calendar')}
                  </div>
                </div>
                <ul className="space-y-4 font-sans text-lg text-[#121212]/80" data-id="element-1634">
                  <li data-id="element-1635">{t('Adresse vérifiée', 'Verified Address')}</li>
                  <li data-id="element-1636">{t('Adresses multiples', 'Multiple Addresses')}</li>
                  <li data-id="element-1637">
                    {t('Tous les types de collecte', 'All Collection Types')}
                  </li>
                </ul>
              </div>

              {/* COLLECTION TYPES */}
              <div className="border border-[#E4E6EC] p-8" data-id="element-1638">
                <div className="flex items-center gap-3 mb-6" data-id="element-1639">
                  <RecycleIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-1640" />
                  <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase" data-id="element-1641">
                    {t('Types de collecte', 'Collection Types')}
                  </div>
                </div>
                <ul className="space-y-4 font-sans text-lg text-[#121212]/80" data-id="element-1642">
                  <li data-id="element-1643">{t('Recyclage', 'Recycling')}</li>
                  <li data-id="element-1644">{t('Déchets', 'Waste')}</li>
                  <li data-id="element-1645">{t('Encombrants', 'Bulky Items')}</li>
                  <li data-id="element-1646">{t('Résidus verts', 'Yard Waste')}</li>
                  <li data-id="element-1647">{t('Arbres', 'Trees')}</li>
                  <li data-id="element-1648">{t('Grand ménage saisonnier', 'Seasonal Cleanup')}</li>
                </ul>
              </div>

              {/* REMINDERS */}
              <div className="border border-[#E4E6EC] p-8" data-id="element-1649">
                <div className="flex items-center gap-3 mb-6" data-id="element-1650">
                  <BellIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-1651" />
                  <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase" data-id="element-1652">
                    {t('Rappels', 'Reminders')}
                  </div>
                </div>
                <ul className="space-y-4 font-sans text-lg text-[#121212]/80" data-id="element-1653">
                  <li data-id="element-1654">{t('Rappels automatiques', 'Automatic Reminders')}</li>
                  <li data-id="element-1655">{t("Selon l'adresse", 'Address-Based')}</li>
                  <li data-id="element-1656">{t('Selon la collecte', 'Collection-Specific')}</li>
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" data-id="element-1657">
              {/* ATNA ASSISTANT — the ONLY Powered by ATNA badge */}
              <div className="border border-[#4F46E5]/20 bg-[#4F46E5]/[0.02] p-10 relative" data-id="element-1658">
                <div className="absolute -top-3 left-10 bg-[#4F46E5] text-white font-sans text-[10px] font-bold tracking-widest uppercase px-3 py-1.5" data-id="element-1659">
                  {t('Propulsé par ATNA', 'Powered by ATNA')}
                </div>
                <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mt-2" data-id="element-1660">
                  {t('Assistant', 'Assistant')}
                </div>
                <div className="font-sans text-xs text-[#121212]/45 mb-6 mt-2" data-id="element-1661">
                  {t('En production à Clarence-Rockland · Illustratif ailleurs', 'In production at Clarence-Rockland · Illustrative elsewhere')}
                </div>
                <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-1662">
                  {[{
                  fr: 'Bac manqué',
                  en: 'Missed Bin'
                }, {
                  fr: 'Quel bac ?',
                  en: 'Which Bin?'
                }, {
                  fr: 'Prochaine collecte',
                  en: 'Next Collection'
                }, {
                  fr: 'Point de dépôt',
                  en: 'Drop-Off Location'
                }, {
                  fr: 'Problème de collecte',
                  en: 'Collection Issue'
                }].map(q => <div key={q.en} className="font-sans text-base text-[#121212]/80 flex items-center gap-2" data-id="element-1663">
                      <MessageSquareIcon className="w-4 h-4 text-[#4F46E5]/60 shrink-0" data-id="element-1664" />
                      {t(q.fr, q.en)}
                    </div>)}
                </div>
                <div className="pt-6 border-t border-[#4F46E5]/10" data-id="element-1665">
                  <p className="font-serif text-xl text-[#121212]" data-id="element-1666">
                    {t("Chaque question résolue par l'Assistant, c'est un appel de moins au service à la clientèle.", 'Every question resolved by the Assistant is one fewer call to the service desk.')}
                  </p>
                </div>
              </div>

              {/* RECYCLEMAP+ */}
              <div className="border border-[#E4E6EC] p-10" data-id="element-1667">
                <div className="flex items-center gap-3 mb-6" data-id="element-1668">
                  <MapPinIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-1669" />
                  <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase" data-id="element-1670">
                    RecycleMap+
                  </div>
                </div>
                <p className="font-sans text-lg text-[#121212]/70 mb-6" data-id="element-1671">
                  {t('Localisateur de points de dépôt.', 'Drop-off locator.')}
                </p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4 font-sans text-base text-[#121212]/80" data-id="element-1672">
                  <span data-id="element-1673">{t('Municipal', 'Municipal')}</span>
                  <span data-id="element-1674">
                    {t('Exploité par producteur', 'Producer-operated')}
                  </span>
                  <span data-id="element-1675">{t('Sites privés', 'Private sites')}</span>
                  <span data-id="element-1676">{t('Filtrer par matière', 'Filter by material')}</span>
                  <span data-id="element-1677">{t('Filtrer par lieu', 'Filter by location')}</span>
                </div>
              </div>
            </div>

            {/* ANALYTICS */}
            <div className="border border-[#E4E6EC] p-10 mb-8" data-id="element-1678">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-1679">
                <div data-id="element-1680">
                  <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-6" data-id="element-1681">
                    {t('Aperçus opérationnels', 'Operational Insights')}
                  </div>
                  <ul className="space-y-4 font-sans text-lg text-[#121212]/80" data-id="element-1682">
                    <li data-id="element-1683">{t('Abonnés par secteur', 'Subscribers by Area')}</li>
                    <li data-id="element-1684">{t('Engagement par secteur', 'Engagement by Area')}</li>
                    <li data-id="element-1685">{t('Comparaison de périodes', 'Period Comparison')}</li>
                    <li data-id="element-1686">
                      {t('Détection des secteurs à faible engagement', 'Low-Engagement Area Detection')}
                    </li>
                  </ul>
                  <p className="font-sans text-base text-[#121212]/60 leading-relaxed mt-6 pt-6 border-t border-[#E4E6EC]" data-id="element-1687">
                    {t("L'engagement par secteur révèle où concentrer les efforts d'éducation et de communication.", 'Area-level engagement reveals where education and communication efforts should be focused.')}
                  </p>
                </div>
                <div className="border border-[#E4E6EC] bg-[#FAFAFB] p-8 flex flex-col" data-id="element-1688">
                  <div className="font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase mb-4 flex items-center gap-2" data-id="element-1689">
                    <CheckCircle2Icon className="w-4 h-4" data-id="element-1690" />
                    {t('Aperçu pour la direction', 'Executive Insight')}
                  </div>
                  <p className="font-serif text-xl text-[#121212] leading-snug mb-4" data-id="element-1691">
                    {t('Les données par secteur révèlent où agir.', 'Area-level data reveals where to act.')}
                  </p>
                  <p className="font-sans text-base text-[#121212]/70 leading-relaxed" data-id="element-1692">
                    {t("Combinée à Communications, l'éducation peut être livrée précisément là où la confusion et la contamination se concentrent.", 'Combined with Communications, education can be delivered exactly where confusion and contamination concentrate.')}
                  </p>
                  <p className="font-sans text-[11px] text-[#121212]/40 mt-auto pt-6" data-id="element-1693">
                    {t('Exemple illustratif', 'Illustrative Example')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: FINAL POSITIONING */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1694">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1695">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-10 leading-tight" data-id="element-1696">
              {t('On ne retire pas un service que chaque foyer attend.', "You don't remove a service every household expects.")}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="space-y-2 font-sans text-xl text-[#121212]/70 mb-12" data-id="element-1697">
              <p data-id="element-1698">
                {t('Une fois les calendriers de collecte intégrés à la routine hebdomadaire du foyer, le service devient essentiel.', 'Once collection schedules become part of the weekly household routine, the service becomes essential.')}
              </p>
              <p className="font-medium text-[#121212] mt-6" data-id="element-1699">
                {t('De confiance.', 'Trusted.')}
              </p>
              <p className="font-medium text-[#121212]" data-id="element-1700">
                {t('Attendu.', 'Expected.')}
              </p>
              <p className="font-medium text-[#121212]" data-id="element-1701">
                {t('Difficile à remplacer.', 'Difficult to replace.')}
              </p>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="mb-16" data-id="element-1702">
              <div className="w-16 h-px bg-[#E4E6EC] mx-auto mb-8" data-id="element-1703" />
              <p className="font-serif text-2xl text-[#121212]/60" data-id="element-1704">
                {t("Le calendrier crée l'habitude.", 'The calendar creates the habit.')}
              </p>
              <p className="font-serif text-2xl text-[#121212]/60" data-id="element-1705">
                {t("L'adresse crée la relation.", 'The address creates the relationship.')}
              </p>
              <p className="font-serif text-2xl text-[#121212]" data-id="element-1706">
                {t('La relation propulse chaque service futur.', 'The relationship powers every future service.')}
              </p>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} data-id="element-1707">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Collect Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-1708">
                {t('Demander une démo', 'Request a Demo')}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1709" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1710" />
    </div>;
}